/**
 * ActiveContestContext — shared source-of-truth for the Contest
 * Promo surfaces (top-bar popup + slot overlays).
 *
 * Added 2026-07-21 for the Contest Promo dispatch. Owns:
 *   1. A single fetch of `/api/public/contest/active` on mount.
 *   2. In-memory dismissal state for the top-bar popup. Per the
 *      dispatch's hard rule, this dismissal is REACT-STATE ONLY —
 *      NOT localStorage, NOT sessionStorage. Reloading the page or
 *      opening a new tab re-shows the top bar; that's intentional.
 *
 * Consumers:
 *   * `ContestPromoTopBar` — reads `{active, contestName, dismissed,
 *      dismiss}` and self-suppresses when either flag is falsy.
 *   * `ContestPromoSlotOverlay` — reads `{active}` and its own
 *      `hasWinnerPhoto` prop; the parent decides whether the slot
 *      already has a winner assigned.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const ActiveContestCtx = createContext({
  active: false,
  contestId: null,
  contestName: null,
  // `prizeAmountCents` — canonical read path for prize display on
  // landing / hero / promo copy. Added 2026-07-23 for the prize-
  // hardcode sweep so a PATCH to `/admin/contest/config.prize_amount_cents`
  // on prod propagates without a frontend redeploy. Falls back to
  // `null` while the /public/contest/active fetch is in flight or
  // when the contest is inactive — consumers should render a
  // sensible default in that case.
  prizeAmountCents: null,
  // Round-detail fields — added 2026-07-23 for the Rules Page
  // (Item A). All ISO-8601 strings (or null). Frontend consumers
  // format for display; the /cutest-pluck/rules page renders dates
  // in America/New_York time with a "TBD" fallback for null values.
  entryOpenAt: null,
  entryCloseAt: null,
  votingCloseAt: null,
  winnerCount: null,
  loading: true,
  dismissed: false,
  dismiss: () => {},
});

export function ActiveContestProvider({ children }) {
  const [state, setState] = useState({
    active: false,
    contestId: null,
    contestName: null,
    prizeAmountCents: null,
    entryOpenAt: null,
    entryCloseAt: null,
    votingCloseAt: null,
    winnerCount: null,
    loading: true,
  });
  // Dismissal is IN-MEMORY per the 2026-07-21 dispatch. Storing to
  // localStorage/sessionStorage would persist across reloads, which
  // the dispatch explicitly forbids. A source-scan drift-guard
  // pins that neither of these APIs is called for this key.
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/public/contest/active")
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        setState({
          active: Boolean(d.active),
          contestId: d.contest_id || null,
          contestName: d.name || null,
          prizeAmountCents:
            typeof d.prize_amount_cents === "number"
              ? d.prize_amount_cents
              : null,
          entryOpenAt: typeof d.entry_open_at === "string" ? d.entry_open_at : null,
          entryCloseAt: typeof d.entry_close_at === "string" ? d.entry_close_at : null,
          votingCloseAt: typeof d.voting_close_at === "string" ? d.voting_close_at : null,
          winnerCount: typeof d.winner_count === "number" ? d.winner_count : null,
          loading: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({
          active: false,
          contestId: null,
          contestName: null,
          prizeAmountCents: null,
          entryOpenAt: null,
          entryCloseAt: null,
          votingCloseAt: null,
          winnerCount: null,
          loading: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = {
    ...state,
    dismissed,
    dismiss: () => setDismissed(true),
  };
  return (
    <ActiveContestCtx.Provider value={value}>
      {children}
    </ActiveContestCtx.Provider>
  );
}

export function useActiveContest() {
  return useContext(ActiveContestCtx);
}
